import React, { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import { addCuratedItem, getExternalContent, previewExternalLink, pullYoutubeChannels, saveExternalContentSettings, type ExternalContentView } from '../../../api/externalContentApi';
import { deleteNewsArticle } from '../../../api/adminApi';
import { S, fmtWhen } from './adminPageStyles';

/** Podcasts, video & LinkedIn (SRS #31): curated links, YouTube channel allow-list, enabled media kinds. */

const AdminExternalContentPage: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, activeSection, setSelectedRegulation, handleNavClick, handleLogout } = useAdminNavigation();
  const [view, setView] = useState<ExternalContentView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [kind, setKind] = useState<'linkedin' | 'video' | 'podcast'>('linkedin');
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [country, setCountry] = useState('Global');
  const [channelInput, setChannelInput] = useState('');
  const [channelLabel, setChannelLabel] = useState('');

  const load = useCallback(() => { getExternalContent().then(setView).catch(e => setError(e instanceof Error ? e.message : 'Failed to load')); }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setError(null); setOk(null);
    try { await fn(); setOk(done); load(); } catch (e) { setError(e instanceof Error ? e.message : 'Failed'); }
  };
  const preview = async () => {
    try { const p = await previewExternalLink(url); if (p.title) setTitle(p.title); if (p.kind === 'video' || p.kind === 'podcast' || p.kind === 'linkedin') setKind(p.kind); }
    catch (e) { setError(e instanceof Error ? e.message : 'Preview failed'); }
  };
  const add = () => act(async () => { await addCuratedItem({ kind, url, title: title || undefined, country }); setUrl(''); setTitle(''); }, 'Added to the feed.');
  const addChannel = () => view && act(() => saveExternalContentSettings({ youtube_channels: [...view.settings.youtube_channels, { id: channelInput, label: channelLabel }] }), 'Channel added.').then(() => { setChannelInput(''); setChannelLabel(''); });
  const removeChannel = (id: string) => view && act(() => saveExternalContentSettings({ youtube_channels: view.settings.youtube_channels.filter(c => c.id !== id) }), 'Channel removed.');
  const toggleKind = (k: string) => view && act(() => saveExternalContentSettings({ kinds_enabled: view.settings.kinds_enabled.includes(k) ? view.settings.kinds_enabled.filter(x => x !== k) : [...view.settings.kinds_enabled, k] }), 'Saved.');
  const remove = (id: string, t: string) => { if (window.confirm(`Remove "${t}" from the feed?`)) act(() => deleteNewsArticle(localStorage.getItem('policyora_token') || '', id), 'Removed.'); };

  return (
    <DashboardLayout collapsed={sidebarCollapsed} activeSection={activeSection} welcomeLabel="External content" selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((p) => !p)} onNavClick={handleNavClick} onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout} onRegulationModalClose={() => setSelectedRegulation(null)}>
      <section style={S.section}>
        <h2 style={S.h2}>Podcasts, video &amp; LinkedIn</h2>
        <p style={S.sub}>Relevant external content alongside the news feed. Podcasts are discovered automatically; YouTube is pulled from channels you allow-list (keyless RSS, through the AI-policy relevance gate); LinkedIn posts are added here by link. Every feed item and regulation offers one-click Share on LinkedIn.</p>
        {view && <p style={{ ...S.sub, fontStyle: 'italic' }}>{view.linkedin_note}</p>}
        {error && <div style={S.error}>{error}</div>}
        {ok && <div style={S.ok}>{ok}</div>}

        {view && (
          <>
            <div style={{ border: '1px solid var(--ds-border)', borderRadius: 10, padding: 14, marginBottom: 16, display: 'grid', gap: 10 }}>
              <div style={{ fontWeight: 700 }}>Add a link to the feed</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <select aria-label="Kind" style={S.input} value={kind} onChange={e => setKind(e.target.value as typeof kind)}>
                  <option value="linkedin">LinkedIn post / article</option><option value="video">Video (YouTube, Vimeo)</option><option value="podcast">Podcast episode</option>
                </select>
                <input aria-label="URL" style={{ ...S.input, flex: '1 1 300px' }} placeholder="https://www.linkedin.com/posts/…" value={url} onChange={e => setUrl(e.target.value)} />
                <button style={S.btn} onClick={preview} disabled={!url}>Read title</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <input aria-label="Title" style={{ ...S.input, flex: '1 1 300px' }} placeholder="Title (read from the page if left blank)" value={title} onChange={e => setTitle(e.target.value)} />
                <input aria-label="Country" style={S.input} value={country} onChange={e => setCountry(e.target.value)} />
                <button style={S.primary} onClick={add} disabled={!url}>Add</button>
              </div>
              <div style={S.muted}>Items must read as AI/technology <em>policy</em> — the same gate the scrapers use.</div>
            </div>

            <div style={{ border: '1px solid var(--ds-border)', borderRadius: 10, padding: 14, marginBottom: 16, display: 'grid', gap: 10 }}>
              <div style={{ fontWeight: 700 }}>YouTube channels (allow-list)</div>
              {view.settings.youtube_channels.length === 0 && <div style={S.muted}>No channels yet — the old keyword search was retired for junk; add regulators, parliaments, law firms and policy institutes here.</div>}
              {view.settings.youtube_channels.map(c => (
                <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={S.mono}>{c.id}</span><span>{c.label}</span><span style={{ flex: 1 }} />
                  <button style={S.danger} onClick={() => removeChannel(c.id)}>Remove</button>
                </div>
              ))}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <input aria-label="Channel id or URL" style={{ ...S.input, flex: '1 1 260px' }} placeholder="UC… channel id or channel URL" value={channelInput} onChange={e => setChannelInput(e.target.value)} />
                <input aria-label="Channel label" style={S.input} placeholder="Label" value={channelLabel} onChange={e => setChannelLabel(e.target.value)} />
                <button style={S.btn} onClick={addChannel} disabled={!channelInput}>Add channel</button>
                <button style={S.btn} onClick={() => act(async () => { const r = await pullYoutubeChannels(); setOk(`Pulled ${r.channels} channels: ${r.stored} new videos, ${r.skipped_irrelevant} off-topic, ${r.skipped_duplicate} already known.`); }, 'Pulled.')}>Pull now</button>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <span style={{ fontWeight: 700, marginRight: 10 }}>Kinds shown in the feed</span>
              {view.kinds.map(k => (
                <label key={k} style={{ marginRight: 12, fontSize: 13 }}>
                  <input type="checkbox" checked={view.settings.kinds_enabled.includes(k)} disabled={k === 'article'} onChange={() => toggleKind(k)} /> {k}
                </label>
              ))}
            </div>

            <h3 style={{ fontSize: 15, margin: '0 0 8px' }}>Curated items</h3>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Kind</th><th style={S.th}>Title</th><th style={S.th}>Country</th><th style={S.th}>Published</th><th style={S.th}></th></tr></thead>
              <tbody>
                {view.curated.length === 0 && <tr><td style={S.td} colSpan={5}><span style={S.muted}>Nothing curated yet.</span></td></tr>}
                {view.curated.map(c => (
                  <tr key={c.id}>
                    <td style={S.td}><span style={S.chip}>{c.kind}</span></td>
                    <td style={S.td}><a href={c.url} target="_blank" rel="noreferrer">{c.title}</a></td>
                    <td style={S.td}>{c.country}</td>
                    <td style={S.td}>{c.published_date ? fmtWhen(c.published_date) : '—'}</td>
                    <td style={S.td}><button style={S.danger} onClick={() => remove(c.id, c.title)}>Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>
    </DashboardLayout>
  );
};

export default AdminExternalContentPage;
